import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { shareGuide } from "@/lib/share";
import { HostAssistant } from "@/components/HostAssistant";
import { GuideChecklist } from "@/components/GuideChecklist";
import { QRStudio } from "@/components/QRStudio";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  ArrowLeft, Loader2, Pencil, Eye, Share2, QrCode, Sparkles, Check, AlertTriangle,
  ChevronRight, Wand2, Eye as EyeIcon, MessageCircle, HelpCircle, X, Languages, Globe, Download,
} from "lucide-react";
import { toast } from "sonner";

const CAT = { critica: { label: "Critica", cls: "bg-rose-50 text-rose-600" }, importante: { label: "Importante", cls: "bg-amber-50 text-amber-600" }, consigliata: { label: "Consigliata", cls: "bg-blue-50 text-blue-600" } };
const ORDER = { critica: 0, importante: 1, consigliata: 2 };
const SUB = {
  parking: "Gli ospiti spesso chiedono dove lasciare l'auto.",
  trash: "Aggiungi le istruzioni sui rifiuti per ridurre le domande.",
  transport: "Indica come raggiungere la struttura coi mezzi.",
  access: "Spiega come entrare: porta, citofono, codici.",
  checkin: "Istruzioni di arrivo chiare riducono i problemi.",
  checkout: "Indica orario e cosa fare prima di partire.",
  wifi: "La domanda più frequente in assoluto.",
  contact: "Come possono raggiungerti gli ospiti in caso di bisogno.",
  emergency: "Numeri utili in caso di emergenza.",
  climate: "Come usare aria condizionata e riscaldamento.",
};

export default function StructureHub() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [health, setHealth] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showHealth, setShowHealth] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/properties/${id}/publish`);
      setProp(data.property);
      toast.success("Guida pubblicata!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  const load = useCallback(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProp(data.property))
      .catch(() => { toast.error("Struttura non trovata"); navigate("/dashboard/properties"); });
    api.get(`/properties/${id}/health`).then(({ data }) => setHealth(data)).catch(() => {});
    api.get(`/properties/${id}/insights`).then(({ data }) => setInsights(data)).catch(() => {});
  }, [id, navigate]);
  useEffect(load, [load]);

  const applyImprove = async (key, text) => {
    const map = {
      welcome: (p) => ({ ...p, welcome_message: text }),
      checkin: (p) => ({ ...p, checkin: { ...p.checkin, instructions: text } }),
      checkout: (p) => ({ ...p, checkout: { ...p.checkout, instructions: text } }),
    };
    if (!map[key]) return false;
    try {
      const updated = map[key](prop);
      const { data } = await api.put(`/properties/${id}`, updated);
      setProp(data.property);
      api.get(`/properties/${id}/health`).then(({ data }) => setHealth(data)).catch(() => {});
      toast.success("Guida aggiornata");
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };

  const viewAsGuest = () => {
    if (prop.status === "published") window.open(`/g/${prop.slug}`, "_blank");
    else { toast.info("Apri l'anteprima dall'editor"); navigate(`/dashboard/properties/${id}/edit`); }
  };
  const showQr = () => {
    if (prop.status !== "published") { toast.info("Pubblica la guida per generare il QR"); return; }
    setQrOpen(true);
  };

  const toggleSmartJourney = async () => {
    const next = !(prop.smart_journey ?? true);
    setProp((p) => ({ ...p, smart_journey: next }));
    try { await api.put(`/properties/${id}`, { smart_journey: next }); }
    catch { setProp((p) => ({ ...p, smart_journey: !next })); toast.error("Impossibile aggiornare"); }
  };

  if (!prop) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  const h = health || { percent: 0, checks: [], counts: {} };
  const published = prop.status === "published";
  const percent = h.percent;
  const missing = h.checks.filter((c) => c.status === "missing").sort((a, b) => ORDER[a.category] - ORDER[b.category]);

  const priority = [];
  (insights?.gaps || []).forEach((g) => {
    if (g.not_answered > 0) priority.push({ icon: "warn", title: `${g.count} ospiti hanno chiesto: ${g.label}`, sub: `Il Concierge AI non ha trovato risposta ${g.not_answered} volt${g.not_answered === 1 ? "a" : "e"}. Aggiungi questa informazione.`, cta: "Aggiungi" });
  });
  missing.forEach((m) => priority.push({ icon: m.category === "critica" ? "warn" : "spark", title: `${m.label} da configurare`, sub: SUB[m.key] || "Aggiungi questa informazione per una guida più completa.", cta: m.category === "consigliata" ? "Migliora con AI" : "Completa ora" }));
  const top3 = priority.slice(0, 3);

  const langs = [["Italiano", true], ["English", !!(prop.translations?.en || (prop.languages || []).includes("en"))], ["Français", !!(prop.translations?.fr)], ["Deutsch", !!(prop.translations?.de)]];

  const QA = [
    { label: "Modifica guida", icon: Pencil, on: () => navigate(`/dashboard/properties/${id}/edit`), primary: true },
    { label: "Vedi come ospite", icon: EyeIcon, on: viewAsGuest },
    { label: "Condividi", icon: Share2, on: () => shareGuide(`${window.location.origin}/g/${prop.slug}`, prop.name) },
    { label: "QR code", icon: QrCode, on: showQr },
    { label: "Assistente Host AI", icon: Sparkles, on: () => setShowAssistant(true) },
  ];

  return (
    <div className="min-h-screen bg-slate-50 max-w-md mx-auto pb-10" data-testid="structure-hub">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-100 h-14 flex items-center justify-between px-4">
        <button onClick={() => navigate("/dashboard/properties")} className="text-slate-500"><ArrowLeft className="w-5 h-5" /></button>
        <span className="font-display font-bold text-slate-900 truncate px-2">{prop.name}</span>
        <button onClick={() => navigate(`/dashboard/properties/${id}/edit`)} className="text-blue-600 font-semibold text-sm">Modifica</button>
      </header>

      <div className="px-4 py-4 space-y-4">
        {/* Summary */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5">
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-semibold ${published ? "text-blue-600" : "text-slate-400"}`}>● {published ? "Pubblicata" : "Bozza"}</span>
          </div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-0.5">{prop.name}</h1>
          <p className="text-sm text-slate-500 mt-2">La tua guida è completa al <span className="font-bold text-blue-600">{percent}%</span></p>
          <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500 transition-all duration-700" style={{ width: `${percent}%` }} /></div>
          <button onClick={() => setShowHealth(true)} data-testid="see-all-suggestions" className="mt-3 text-sm font-semibold text-blue-600 inline-flex items-center gap-1">Vedi tutti i suggerimenti <ChevronRight className="w-4 h-4" /></button>
        </div>

        {!published && <GuideChecklist prop={prop} onEdit={() => navigate(`/dashboard/properties/${id}/edit`)} onViewGuest={viewAsGuest} onPublish={publish} publishing={publishing} />}

        {/* Resume AI wizard */}
        {prop.wizard?.active && !prop.wizard?.completed && (
          <button onClick={() => navigate(`/dashboard/properties/${id}/wizard`)} data-testid="resume-wizard" className="w-full rounded-3xl border-2 border-blue-500 bg-blue-50/70 p-4 text-left flex items-center gap-3 active:scale-[0.99] transition-transform">
            <div className="w-11 h-11 rounded-2xl bg-blue-600 text-white grid place-items-center shrink-0"><Sparkles className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0"><p className="font-display font-bold text-slate-900">Continua configurazione</p><p className="text-[13px] text-slate-500">Riprendi la creazione guidata con l'AI da dove eri rimasto.</p></div>
            <ChevronRight className="w-5 h-5 text-blue-600" />
          </button>
        )}

        {/* Quick actions */}
        <div className="flex gap-2.5 overflow-x-auto -mx-1 px-1 pb-1">
          {QA.map((a) => (
            <button key={a.label} onClick={a.on} data-testid={`qa-${a.label}`} className={`shrink-0 flex flex-col items-center gap-1.5 rounded-2xl px-4 py-3 min-w-[74px] ${a.primary ? "bg-blue-500 text-white" : "bg-white border border-slate-100 text-slate-600"}`}>
              <a.icon className="w-5 h-5" /><span className="text-[11px] font-semibold">{a.label}</span>
            </button>
          ))}
        </div>

        {/* Priority insights */}
        {top3.length > 0 && (
          <div className="space-y-2.5">
            {top3.map((it, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-100 p-4 flex items-start gap-3" data-testid={`priority-insight-${i}`}>
                <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${it.icon === "warn" ? "bg-amber-50 text-amber-500" : "bg-blue-50 text-blue-500"}`}>{it.icon === "warn" ? <AlertTriangle className="w-4.5 h-4.5" /> : <Sparkles className="w-4.5 h-4.5" />}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 text-[15px] leading-tight">{it.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{it.sub}</p>
                  <button onClick={() => it.cta.includes("AI") ? setShowAssistant(true) : navigate(`/dashboard/properties/${id}/edit`)} className="mt-2 text-sm font-semibold text-blue-600 inline-flex items-center gap-1">{it.cta} <ChevronRight className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Host AI banner */}
        <button onClick={() => setShowAssistant(true)} data-testid="open-host-assistant" className="w-full rounded-3xl bg-blue-600 text-white p-5 text-left flex items-center gap-3 active:scale-[0.99] transition-transform">
          <div className="w-11 h-11 rounded-2xl bg-white/20 grid place-items-center shrink-0"><Wand2 className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0"><p className="font-display font-bold">Chiedi all'Assistente Host AI</p><p className="text-[13px] text-blue-100">Crea, migliora e completa la tua guida</p></div>
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Guest insights */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1"><MessageCircle className="w-4 h-4 text-blue-500" /><h2 className="font-display font-bold text-slate-900">Cosa chiedono i tuoi ospiti</h2></div>
          <p className="text-xs text-slate-400 mb-4">Ultimi 30 giorni · dati aggregati e anonimi</p>
          {!insights?.has_data ? (
            <div className="text-center py-6" data-testid="insights-empty">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 grid place-items-center mx-auto"><HelpCircle className="w-6 h-6" /></div>
              <p className="font-semibold text-slate-700 mt-3">I tuoi Guest Insights appariranno qui</p>
              <p className="text-sm text-slate-400 mt-1">Quando gli ospiti inizieranno a usare la guida e il Concierge AI, vedrai quali informazioni cercano più spesso.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2">
                {[["Visite", insights.analytics.views], ["Conversazioni", insights.analytics.conversations], ["Domande", insights.analytics.questions], ["Senza risposta", insights.analytics.not_answered]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl bg-slate-50 p-2.5 text-center"><p className="font-display font-extrabold text-xl text-slate-900">{v}</p><p className="text-[10px] text-slate-400 leading-tight mt-0.5">{l}</p></div>
                ))}
              </div>
              {insights.analytics.access_unlocks > 0 && (
                <div className="mt-3 rounded-2xl bg-blue-50 border border-blue-100 p-3 flex items-center justify-between" data-testid="insight-unlocks">
                  <div><p className="text-sm font-semibold text-blue-900">Sblocchi info di accesso (PIN)</p><p className="text-[11px] text-blue-600/70">Ospiti che hanno inserito il PIN per vedere i dati protetti</p></div>
                  <span className="font-display font-extrabold text-xl text-blue-700">{insights.analytics.access_unlocks}</span>
                </div>
              )}
                <div className="mt-4 space-y-2">
                  {insights.topics.slice(0, 5).map((t) => (
                    <div key={t.topic} className="flex items-center justify-between"><span className="text-sm text-slate-700">{t.label}</span><span className="text-sm font-bold text-slate-900">{t.count}</span></div>
                  ))}
                </div>
              )}
              {insights.gaps.length > 0 && (
                <div className="mt-4 space-y-2">
                  {insights.gaps.map((g) => (
                    <div key={g.topic} className="rounded-2xl bg-amber-50 border border-amber-100 p-3.5" data-testid={`gap-${g.topic}`}>
                      <p className="text-sm font-semibold text-amber-900">{g.not_answered > 0 ? `${g.not_answered} ospiti hanno chiesto “${g.label}” e il Concierge AI non ha trovato risposta.` : `Molti ospiti chiedono di ${g.label}.`}</p>
                      <button onClick={() => navigate(`/dashboard/properties/${id}/edit`)} className="mt-2 text-sm font-semibold text-blue-600 inline-flex items-center gap-1">Aggiungi {g.label} <ChevronRight className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Smart Guest Journey */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-500 grid place-items-center shrink-0"><Sparkles className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-slate-900">Adatta la guida al soggiorno</h2>
              <p className="text-sm text-slate-500 mt-0.5">L'ospite vede prima le informazioni utili nel momento giusto: prima dell'arrivo, durante e al check-out.</p>
            </div>
            <button onClick={toggleSmartJourney} data-testid="toggle-smart-journey" role="switch" aria-checked={prop.smart_journey ?? true}
              className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${(prop.smart_journey ?? true) ? "bg-blue-600" : "bg-slate-200"}`}>
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${(prop.smart_journey ?? true) ? "translate-x-5" : ""}`} />
            </button>
          </div>
        </div>

        {/* Multilingua */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-3"><Languages className="w-4 h-4 text-blue-500" /><h2 className="font-display font-bold text-slate-900">Lingue della guida</h2></div>
          <div className="grid grid-cols-2 gap-2">
            {langs.map(([l, on]) => (
              <div key={l} className={`flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium ${on ? "bg-blue-50 text-blue-700" : "bg-slate-50 text-slate-400"}`}>{l}{on ? <Check className="w-4 h-4" /> : <span>—</span>}</div>
            ))}
          </div>
          <button onClick={() => navigate(`/dashboard/properties/${id}/edit`)} className="mt-3 w-full rounded-xl border border-slate-200 text-slate-700 font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-2"><Globe className="w-4 h-4" /> Genera traduzione</button>
        </div>
      </div>

      {/* Guide Health sheet */}
      <Sheet open={showHealth} onOpenChange={setShowHealth}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto">
          <SheetHeader><SheetTitle>Completezza della guida — {percent}%</SheetTitle></SheetHeader>
          <div className="py-4 space-y-2">
            {h.checks.slice().sort((a, b) => (a.status === b.status ? ORDER[a.category] - ORDER[b.category] : a.status === "missing" ? -1 : 1)).map((c) => (
              <div key={c.key} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3" data-testid={`health-check-${c.key}`}>
                <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${c.status === "ok" ? "bg-blue-50 text-blue-500" : "bg-amber-50 text-amber-500"}`}>{c.status === "ok" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}</div>
                <div className="flex-1 min-w-0"><p className="font-semibold text-slate-800 text-sm">{c.label}</p><span className={`text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 ${CAT[c.category].cls}`}>{CAT[c.category].label}</span></div>
                {c.status === "missing" && <button onClick={() => { setShowHealth(false); navigate(`/dashboard/properties/${id}/edit`); }} className="text-xs font-semibold text-blue-600 shrink-0">Aggiungi</button>}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* QR Studio */}
      <QRStudio property={prop} open={qrOpen} onClose={() => setQrOpen(false)} />

      <HostAssistant open={showAssistant} onClose={() => setShowAssistant(false)} prop={prop} onApplyImprove={applyImprove} />
    </div>
  );
}
